import { useCallback, useEffect, useLayoutEffect } from 'react'
import { Button, Icon, Popconfirm } from 'antd'
import {
  FormattedHTMLMessage,
  FormattedMessage,
  defineMessages,
  useIntl,
} from 'react-intl'

import {
  notiFormForError,
  savePkgConfirm,
} from '../../../components/notificationForm'

import { useUserWithRedirect } from '../../../hooks/user/useUserWithRedirect'
import PackageAndStickerInfo from '../../package/PackageAndStickerInfo'
import {
  usePackageInfo,
  useSubmitPackage,
} from '../../../hooks/reactQuery/usePackage'
import { useHistory, useParams } from 'react-router-dom'
import { useEveryStickerCategory } from '../../../hooks/reactQuery/useEveryStickerCategory'
import { useIsMutating } from 'react-query'

const messages = defineMessages({
  tagsNotExist: {
    id: 'PkgConfirm.noti.tagsNotExist',
    defaultMessage:
      'Sticker tagging is not finished.\n Please complete tagging.',
  },
  editingIsNotComplete: {
    id: 'PkgConfirm.noti.editingIsNotComplete',
    defaultMessage:
      'There are rejected stickers still remaining that should be modified/deleted.\n Please finish the modification/deletion of rejected stickers.',
  },
  notEnoughStickerNumber: {
    id: 'PkgConfirm.noti.PLUS.notEnoughStickerNumber',
    defaultMessage:
      'Oops! For a plus pack, you may upload 24 stickers or more. Please upload at least 24 stickers.',
  },
  toManyStickerNumber: {
    id: 'PkgConfirm.noti.BASIC.toManySticker',
  },
  noStickersInPkg: {
    id: 'ErrorMessage.noStickersInPkg',
    defaultMessage:
      'You haven’t added any stickers.🤯&nbsp;&nbsp;&nbsp;Please add at least one sticker.',
  },
  confirmSuccessTitle: {
    id: 'PkgConfirm.modal.success.title',
    defaultMessage: 'Success',
  },
  confirmSuccessDesc: {
    id: 'PkgConfirm.modal.success.description',
    defaultMessage: 'Congratulations!',
  },
  confirmSuccessBtn: {
    id: 'SelectEmotion.modal.success.ok',
    defaultMessage: 'OK',
  },
  confirmFailTitle: {
    id: 'PkgConfirm.modal.fail.title',
    defaultMessage: 'Fail',
  },
  confirmFailDesc: {
    id: 'PkgConfirm.modal.fail.description',
    defaultMessage: 'Saddly...',
  },
  confirmFailBtn: {
    id: 'PkgConfirm.modal.fail.ok',
    defaultMessage: 'OK',
  },
})

export default function UploadPackageConfirm() {
  const user = useUserWithRedirect()
  const history = useHistory()
  const { packageId } = useParams<{ packageId: string }>()
  const {
    data: packageAndSticker,
    error,
    isLoading,
  } = usePackageInfo(packageId, user?.sessionToken)
  const isMutatingPackage = useIsMutating(packageId) > 0

  const intl = useIntl()
  const { data: everyStickerCategory, isLoading: stickerCategoryLoading } =
    useEveryStickerCategory()
  const submitPackagetMutate = useSubmitPackage(packageId)
  const checkSubmitPossible = useCallback(() => {
    if (!packageAndSticker) return false
    if (
      packageAndSticker.emoticons &&
      packageAndSticker.emoticons.length === 0
    ) {
      notiFormForError(
        intl.locale,
        intl.formatMessage(messages.noStickersInPkg),
      )
      return false
    } else {
      let possible = true

      //플러스팩 일경우 24개 이상 스티커가 있는지 확인
      if (
        packageAndSticker.emoticons.length < 24 &&
        packageAndSticker.packType === 'PLUS'
      ) {
        notiFormForError(
          intl.locale,
          intl.formatMessage(messages.notEnoughStickerNumber),
        )
        possible = false
      }
      //베이직팩 일경우 24개 미만 스티커가 있는지 확인
      if (
        packageAndSticker.emoticons.length >= 24 &&
        packageAndSticker.packType === 'BASIC'
      ) {
        notiFormForError(
          intl.locale,
          intl.formatMessage(messages.toManyStickerNumber),
        )
        possible = false
      }
      // 입력안한 태그가 있는지 확인
      const idx = packageAndSticker.emoticons.findIndex((emoticons) => {
        if (!emoticons.emojiCategories) return true
        if (emoticons.emojiCategories.length === 0) return true
        if (!emoticons.textLang) return true
        return false
      })
      if (idx !== -1) {
        notiFormForError(intl.locale, intl.formatMessage(messages.tagsNotExist))
        possible = false
      }
      // 반려된 스티커가 있는지 확인
      const stickerWithInspection = packageAndSticker.emoticons.findIndex(
        (emoticons) => {
          if (
            emoticons.stickerInspectionData &&
            (emoticons.stickerInspectionData.needImgEditing ||
              emoticons.stickerInspectionData.needTagEditing ||
              emoticons.stickerInspectionData.needStickerDeleting)
          ) {
            return true
          }
          return false
        },
      )
      if (stickerWithInspection !== -1) {
        notiFormForError(
          intl.locale,
          intl.formatMessage(messages.editingIsNotComplete),
        )
        possible = false
      }
      return possible
    }
  }, [packageAndSticker, intl])

  useEffect(() => {
    checkSubmitPossible()
  }, [checkSubmitPossible])

  if (!packageAndSticker || !everyStickerCategory) return <></>
  const handleSubmit = () => {
    if (isMutatingPackage) return
    if (!checkSubmitPossible()) return

    if (user) {
      submitPackagetMutate.mutate(
        {
          packageId,
          sessionToken: user.sessionToken,
        },
        {
          onSuccess: (data) => {
            savePkgConfirm(
              // 기존의 함수가 class 형식으로 history를 참고해서 같이 동작할 수 있게 만듬
              { props: { history } },
              'success',
              intl.formatMessage(messages.confirmSuccessTitle),
              intl.formatMessage(messages.confirmSuccessDesc),
              intl.formatMessage(messages.confirmSuccessBtn),
            )
          },
        },
      )
    }
  }
  return (
    <>
      <div>
        <PackageAndStickerInfo packageInfo={packageAndSticker} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 20,
            marginRight: 10,
            marginTop: 49,
          }}
        >
          <Popconfirm
            placement="top"
            title={
              <div style={{ width: 'max-content', margin: 0 }}>
                <FormattedHTMLMessage
                  id="PkgConfirm.popconfirm.message"
                  defaultMessage="<b>Final Check Before Submission</b><div>Before you click the submit button, please make sure there is<br/>nothing to correct. After submission, you cannot modify pack<br/>introductions, stickers or tags unless the pack is rejected!</div>"
                />
              </div>
            }
            onConfirm={() => handleSubmit()}
            okText={
              <FormattedMessage
                id="PkgConfirm.popconfirm.ok"
                defaultMessage="Submit"
              />
            }
            cancelText={
              <FormattedMessage
                id="PkgConfirm.popconfirm.cancel"
                defaultMessage="I want to check again"
              />
            }
            icon={
              <Icon
                type="exclamation-circle"
                theme="filled"
                style={{ marginTop: 3 }}
              />
            }
          >
            <Button
              className="btn-style-primary"
              style={{ width: 175, margin: '0px 25px', padding: 0 }}
            >
              <FormattedMessage
                id={'PkgConfirm.btn'}
                defaultMessage={'Submit for Review'}
              />
            </Button>
          </Popconfirm>
        </div>
      </div>
    </>
  )
}
